// NOTE: This file purposely has no dependencies on other modules internal or external. If you want to use this directly,
// just copy/paste into your project. Fair warning you won't get updates though, so you may want to use the compiled binaries
// provided with the app.

/**
 * @typedef {object} AsciiData An object representation of ASCII art.
 * @param {string} AsciiData.name The name of the object. Must not contain special chars including a colon, or it will parse wrong.
 * @param {number} AsciiData.width The width of the art in characters.
 * @param {number} AsciiData.height The height of the art in characters.
 * @param {number} AsciiData.outputSize The size of the output data when using non-text format. Should be one of: 8, 16, 32, 64.
 * @param {array} AsciiData.data An array of arrays containing individual characters. ex: [['a', 'b'], ['c', 'd']]
 * @param {object} AsciiData.translations An object mapping characters (single character) to values that can be paresd as numbers. This can be hex (prefix with 0x or $) or binary (prefix with 0b or %) in addition to regular integers.
 * @param {string} AsciiData.asciiOffset A default offset for ASCII characters which are not overridden in translations. This value will be added to or subtracted from each ascii character's character code. This can be an integer, hex (prefix with 0x or $) or binary (prefix witih 0b or %). Negative nubmers are supported in all cases, just prefix with a minus (-).
 */

/**
 * Save data from an Ascii text object to a text file. Probably not something you want to use in most cases outside the app.
 * 
 * Super simple version of the format...
 * meta information
 * double newline signifies end
 * content, including spaces, no whitespace around
 * double newline, no other whitespace signifies end
 * translation table, unicode -> value

 * @param {AsciiData} asciiData The information for the ASCII art drawing
 * @returns {string} Raw text which can be saved to a txt file representing this art.  Can be imported later.
 */
export function saveToText({ name, width, height, outputSize, data, translations, asciiOffset }) {
  if (!width || !height || !outputSize) {
    throw new Error("Missing required parameters! Need width, height and outputSize.");
  }
  if (!data || data.length < 3) {
    throw new Error("Data missing or invalid! Cannot save!");
  }
  if (!translations) {
    translations = {};
  }

  const strData = data.map((row) => row.join("")).join("\n");

  let txt = `Name: ${name}
Width: ${width}
Height: ${height}
Output Size: ${outputSize}
ASCII Offset: ${asciiOffset}

${strData}

${Object.keys(translations)
  .map((k) => `${k}: ${translations[k] === "" ? null : translations[k]}`)
  .join("\n")}`;

  return txt;
}

function slugify(name) {
  return name
    .toString() // Ensure it's a string
    .normalize("NFD") // Split accented characters into their base letters and diacritics
    .replace(/[\u0300-\u036f]/g, "") // Remove the diacritic marks
    .toLowerCase() // Convert to lowercase
    .trim() // Remove leading/trailing whitespace
    .replace(/[^a-z0-9 _]/g, "") // Remove any character that isn't a letter, number, space, or underscore
    .replace(/\s+/g, "_") // Replace groups of spaces with a single underscore
    .replace(/_+/g, "_"); // Merge consecutive underscore
}

/**
 * Load a text file generated by ascii2tilemap into a predictable object format.
 * @param {string} txt The text of a file generated by ascii2tilemap
 * @returns {AsciiData} An object representing the Ascii art. Can be passed to `exportAs`as the second parameter.
 *
 */
export function loadFromText(txt) {
  const lines = txt.split("\n");
  let currLine = 0,
    name,
    width,
    height,
    outputSize, // Default in case things happen in a weird order
    asciiOffset,
    data = [],
    translations = {};

  for (currLine = 0; currLine < lines.length; currLine++) {
    // NOTE: Empty line means section end, start of data
    if (lines[currLine].length === 0) {
      currLine++;
      break;
    }
    const splut = lines[currLine].split(":");
    let [key, value] = splut.map((l) => l.trim());
    if (splut.length != 2) {
      throw new Error("Parameters section is corrupted! Cannot import.");
    }
    switch (key.toLowerCase()) {
      case "name":
        name = value;
        break;
      case "width":
        width = parseInt(value, 10);
        if (isNaN(width)) {
          throw new Error(`Failed parsing key "Width"! It must be a number, received: "${value}"`);
        }
        break;
      case "height":
        height = parseInt(value, 10);
        if (isNaN(height)) {
          throw new Error(`Failed parsing key "Height"! It must be a number, received: "${value}"`);
        }
        break;
      case "ascii offset":
        asciiOffset = parseTextToBinary(value, outputSize ?? 8);
        if (asciiOffset === null) {
          throw new Error(`Failed parsing key "ASCII Offset"! It must be a number, received: "${value}"`);
        }
        asciiOffset = value;
        break;
      case "output size":
        outputSize = parseInt(value, 10);
        if (isNaN(outputSize)) {
          throw new Error(`Failed parsing key "Output Size"! It must be a number, received: "${value}"`);
        }

        break;
    }
  }
  if (!width || !height) {
    throw new Error("Width or height not provided! Cannot import.");
  }
  if (!outputSize) {
    throw new Error("Output Size not provided! Cannot import.");
  }
  if (!asciiOffset) {
    throw new Error("ASCII Offset not provided! Cannot import.");
  }
  for (; currLine < lines.length; currLine++) {
    if (lines[currLine].length === 0) {
      currLine++;
      break;
    }
    data.push([]);
    lines[currLine].split("").map((char) => data[data.length - 1].push(char));
  }

  // Translations...
  for (; currLine < lines.length; currLine++) {
    // Skip blank lines
    if (lines[currLine].length === 0) {
      continue;
    }
    // A colon is a valid character, so we can't just split on that.
    if (lines[currLine][1] !== ":" || lines[currLine].length < 3) {
      throw new Error(`Translations section is corrupted! Cannot import. Problem line: ${lines[currLine]}`);
    }
    let key = lines[currLine][0],
      value = lines[currLine].substring(3);
    if (value === "null") {
      value = null;
    }
    translations[key] = value;
  }

  return { name, width, height, outputSize, data, translations, asciiOffset };
}

/**
 * Helper function to parse a number in text format using hex/binary into a regular number.
 * @param {string} origValue A string value representing a number, such as "5", "0xaf", "$af", "0b11110000" or "%11110000".
 * @param {number} outputSize The number of bits the return value can use. 8/16/32 bit all behave the same, while 64 bit uses BigInt.
 * @returns {number} The numeric value represented
 */
export function parseTextToBinary(origValue, outputSize) {
  let value = origValue,
    isNegative = false,
    finalValue = null;
  if (value.startsWith("-")) {
    isNegative = true;
    value = value.substring(1);
  } else if (value.length === 0) {
    value = "0";
  }
  if (outputSize === 64) {
    return BigInt(origValue);
  } else if (value.startsWith("0x")) {
    finalValue = parseInt(value.substring(2), 16);
  } else if (value.startsWith("$")) {
    finalValue = parseInt(value.substring(1), 16);
  } else if (value.startsWith("0b")) {
    finalValue = parseInt(value.substring(2), 2);
  } else if (value.startsWith("%")) {
    finalValue = parseInt(value.substring(1), 2);
  } else if (!isNaN(value) && !isNaN(parseInt(value))) {
    finalValue = parseInt(value);
  } else {
    return null;
  }
  return isNegative ? 0 - finalValue : finalValue;
}

// Take translations and turn the values into values we can put into the output file.
function parseTranslations(translations, outputSize, asciiOffset) {
  const parsedAsciiOffset = parseTextToBinary(asciiOffset, outputSize);
  return Object.fromEntries(
    Object.entries(translations)
      .filter(([k, v]) => v !== null)
      .map(([key, value]) => {
        value = parseTextToBinary(value, outputSize) ?? value + parsedAsciiOffset;
        return [key, value];
      }),
  );
}

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

/**
 * List of supported export types, shorthand.
 */
export const knownExportTypes = ["bin", "js", "json", "c", "csv", "ascii"];
/**
 * Long-format names for UI use lining up with the shorthand in knownExportTypes.
 */
export const exportNames = ["Binary/Raw", "Javascript module", "JSON", "C Code", "CSV", "Plain Text"];
// Turns the given data into the given format. Does not create a file, just serves up the content.

/**
 * Given an object representing ascii art, create a file in the format specified, allowing usage in other tools.
 * @param {string} exportType A short extension for a file export - one of: bin, js, json, c, csv, or ascii.
 * @param {AsciiData} data The data to export
 * @returns {string} A string or binary representation of the data in the format specified, ready to be written/downloaded to a file.
 */
export function exportAs(exportType, { name, width, height, outputSize, data, translations, asciiOffset }) {
  const parsedTranslations = parseTranslations(translations, outputSize, asciiOffset),
    parsedAsciiOffset = parseTextToBinary(asciiOffset, outputSize),
    rawData = [],
    untouchedData = [];
  data.forEach((row, rowId) => {
    row.forEach((col, colId) => {
      let val;
      if (outputSize === 64) {
        val = parsedTranslations[col] ?? BigInt(col.charCodeAt(0)) + parsedAsciiOffset;
      } else {
        val = parsedTranslations[col] ?? col.charCodeAt(0) + parsedAsciiOffset;
      }
      if (outputSize === 8 && val > 255) {
        val = 255;
      }
      rawData.push(val);
      untouchedData.push(col.charCodeAt(0));
    });
  });
  const rawDataSplit = chunkArray(rawData, 16);

  switch (exportType) {
    case "bin":
      switch (outputSize) {
        case 16:
          return new Uint16Array(rawData);

        case 32:
          return new Uint32Array(rawData);
        case 64:
          return new BigUint64Array(rawData);
        case 8:
        default:
          return new Uint8Array(rawData);
      }
    case "js":
      return `module.exports = { 
    name: "${name}",
    width: ${width}, 
    height: ${height}, 
    outputSize: ${outputSize}, 
    asciiOffset: ${parsedAsciiOffset},
    data: ${
      outputSize === 64
        ? `new BigUint64Array([${rawDataSplit.map((a) => a.map((m) => `BigInt('${m}')`).join(", ")).join(",\n    ")}])`
        : `[${rawDataSplit.map((a) => a.join(", ")).join(",\n    ")}],`
    }
}`;
    case "json":
      if (outputSize === 64) {
        throw new Error(
          "64 bit json exports are not supported due to limitations of the json format. Please use JS or another format",
        );
      }
      return `{ 
    "name": "${name}",
    "width": ${width}, 
    "height": ${height}, 
    "outputSize": ${outputSize}, 
    "asciiOffset": ${parsedAsciiOffset},
    "data": [${rawDataSplit.map((a) => a.join(", ")).join(",\n    ")}]
}
`;
    case "c":
      const slugName = slugify(name),
        slugNameUpper = slugName.toUpperCase();
      let dataType = "unsigned char";
      switch (outputSize) {
        case 16:
          dataType = "unsigned int";
          break;
        case 32:
          dataType = "unsigned long";
          break;
        case 64:
          dataType = "unsigned long long";
          break;
      }
      return `#define ${slugNameUpper}_WIDTH ${width}
#define ${slugNameUpper}_HEIGHT ${height}
#define ${slugNameUpper}_OUTPUT_SIZE ${outputSize}
#define ${slugNameUpper}_ASCII_OFFSET ${parsedAsciiOffset}
const ${dataType} ${slugName}[] = {
    ${chunkArray(rawData, 16)
      .map((n) => n.join(", "))
      .join(",\n    ")}
};`;
    case "csv":
      return rawData.join(",");
    case "ascii":
      return data.map((r) => r.join("")).join("\n");
    default:
      throw new Error("Unknown export type! Available types: " + knownExportTypes.join(", "));
  }
}

/**
 * Wrapper around other methods here - provide this a text file in ascii2tilemap format, and this will produce an export in the given format.
 * If you just want something that works, this is your method!
 * @param {string} text Text from a file exported from ascii2tilemap.
 * @param {string} exportType A short extension for a file export - one of: bin, js, json, c, csv, or ascii.
 * @returns {string} A string or binary representation of the data in the format specified, ready to be written/downloaded to a file.
 */
export function exportTextFile(text, exportType) {
  return exportAs(exportType, loadFromText(text));
}
